/**
 * Demo-mode axios adapter — outbound safety net.
 *
 * When __DEMO_MODE__ is on, every axios call gets routed through this
 * adapter instead of going to the network. The adapter looks the request
 * up in a registry of mock handlers:
 *
 *   - Match found  → handler is invoked, its result is returned as the
 *                    response. No network call is made.
 *   - No match     → safety-net response: a 200 with an explicit
 *                    "blocked" flag the UI can show as a "not available
 *                    in demo" toast. The request never leaves the browser.
 *
 * This is the single chokepoint that keeps the demo offline. New backend
 * endpoints added to the real app fail safely (visible "not available"
 * message) until a handler is registered for them — they never silently
 * hit production.
 *
 * Handler authoring pattern:
 *
 *   register('GET', '/aml/clients/{cid}', async ({ params, query, body }) => {
 *       return { data: { data: clientFor(params.cid), errors: null } };
 *   });
 *
 * Path parameters in `{braces}` become named groups in `params`. Handlers
 * may return either a raw response object ({ status, data, headers }) or
 * just the data payload (which we wrap into a 200 response).
 */

// Internal registry of mock handlers. Each entry: { method, regex, handler, raw }.
const handlers = [];

// Handler-ready gate. install.js resolves this after every handler module
// has loaded and registered its routes. The adapter awaits this before
// looking up a request, so panel-mount endpoints (subscription,
// notifications, dashboard, agreements/status) don't race past the
// registry while their handler modules are still being imported.
//
// We wait indefinitely for markHandlersReady() rather than racing a
// timeout — on a cold-cache hard refresh the dynamic-import chain can
// take >1s, and a premature timeout causes the agreements gate to
// render against a safety-net response (no handler matched yet) when
// the handler is still 200ms from registering. bootstrap.js is
// responsible for calling markHandlersReady() unconditionally — even
// in the install-error path — so the gate can never deadlock the page.
let _readyResolve;
const _ready = new Promise((resolve) => { _readyResolve = resolve; });
let _readyDone = false;
export function markHandlersReady() {
    if (_readyDone) return;
    _readyDone = true;
    _readyResolve();
}
function waitReady() {
    if (_readyDone) return Promise.resolve();
    return _ready;
}

/**
 * Register a mock handler.
 *
 * @param {string} method - HTTP method (case-insensitive: 'GET', 'POST', etc.)
 * @param {string|RegExp} pattern - URL pattern. Strings use {param} syntax for
 *     named captures (e.g. '/aml/clients/{cid}/persons/{pid}'). RegExps are
 *     used as-is; capture groups via (?<name>...) become params.
 * @param {Function} handler - async function ({ params, query, body, config }) => result
 *     `result` may be the raw response data (wrapped into 200) or an object
 *     with explicit { status, data, headers } fields.
 */
export function register(method, pattern, handler) {
    method = method.toUpperCase();
    let regex;
    if (typeof pattern === 'string') {
        // Escape regex meta-chars (including {}) then convert {param} → named
        // capture group. {} MUST be in the escape set — otherwise the second
        // replace step's regex \{(\w+)\} won't match anything (it expects
        // backslash-prefixed curlies after the escape pass), and parameterised
        // routes silently compile to literal-text-match patterns. Found this
        // the hard way in Phase 3.2 verification: /aml/clients/{id} was
        // matching only the literal string "/aml/clients/{id}", never an
        // actual id, so every detail/CDD/AP-history call hit the safety net.
        const escaped = pattern.replace(/[.+*?^$()[\]{}\\]/g, '\\$&');
        const withParams = escaped.replace(/\\\{(\w+)\\\}/g, '(?<$1>[^/]+)');
        regex = new RegExp('^' + withParams + '$');
    } else if (pattern instanceof RegExp) {
        regex = pattern;
    } else {
        throw new Error('[demo] register() pattern must be a string or RegExp');
    }
    handlers.push({ method, regex, handler, raw: pattern });
}

/**
 * Find the first registered handler matching `method` + `url`.
 * Returns { handler, params } or null.
 */
function findMatch(method, url) {
    method = method.toUpperCase();
    for (const h of handlers) {
        if (h.method !== method) continue;
        const m = url.match(h.regex);
        if (m) return { handler: h.handler, params: m.groups || {} };
    }
    return null;
}

/**
 * Normalise the request URL down to a path the registry was registered against.
 * Handles axios baseURL prefixing, absolute URLs, and double slashes.
 */
function normalizeUrl(config) {
    let url = config.url || '';
    // If baseURL is set and url is relative, prepend it
    if (config.baseURL && !/^https?:\/\//.test(url)) {
        url = config.baseURL.replace(/\/+$/, '') + '/' + url.replace(/^\/+/, '');
    }
    // Strip protocol + host
    url = url.replace(/^https?:\/\/[^/]+/, '');
    // Ensure leading slash
    if (!url.startsWith('/')) url = '/' + url;
    // Collapse double slashes (except after protocol, which we already stripped)
    url = url.replace(/\/{2,}/g, '/');
    // Strip query string — handlers see query via config.params
    const qIdx = url.indexOf('?');
    if (qIdx !== -1) url = url.slice(0, qIdx);
    return url;
}

/**
 * Parse request body — axios usually serialises to a JSON string.
 */
function parseBody(data) {
    if (data == null) return null;
    if (typeof data === 'string') {
        try { return JSON.parse(data); }
        catch { return data; }
    }
    return data;
}

/**
 * The axios adapter. Returns a Promise resolving to a response object.
 *
 * Adds a small artificial delay (200–500ms) so spinners flash naturally
 * and the UX feels close to real network. Without this, demo actions
 * complete instantly and feel less convincing.
 */
const demoAdapter = (config) => {
    return new Promise(async (resolve) => {
        const url = normalizeUrl(config);
        const method = (config.method || 'get').toUpperCase();

        // Wait for install.js to finish loading every handler module. Until
        // markHandlersReady() is called, panel-mount requests (which fire
        // the moment Vue mounts) would otherwise lookup against a partially-
        // populated registry and falsely hit the safety net.
        await waitReady();

        // Cosmetic delay so spinners flash and the demo feels like real
        // network. Run it AFTER waitReady so the worst-case wait is
        // bounded (1.5s install ceiling + 200-500ms cosmetic), not stacked
        // on top of an already-slow install path.
        const delay = 200 + Math.floor(Math.random() * 300);
        await new Promise((r) => setTimeout(r, delay));

        const match = findMatch(method, url);
        const body = parseBody(config.data);

        if (match) {
            try {
                const result = await match.handler({
                    params: match.params,
                    query: config.params || {},
                    body,
                    config,
                });
                // Handler returned an explicit response envelope?
                const hasExplicitEnvelope =
                    result &&
                    typeof result === 'object' &&
                    ('status' in result || 'headers' in result) &&
                    'data' in result;
                const status = hasExplicitEnvelope ? (result.status ?? 200) : 200;
                const data = hasExplicitEnvelope ? result.data : result;
                resolve({
                    data,
                    status,
                    statusText: status >= 200 && status < 300 ? 'OK' : 'Error',
                    headers: hasExplicitEnvelope ? (result.headers || {}) : {},
                    config,
                    request: {},
                });
            } catch (err) {
                console.error(`[demo] Handler threw for ${method} ${url}:`, err);
                resolve({
                    data: {
                        data: null,
                        errors: ['Demo handler error: ' + (err?.message || String(err))],
                    },
                    status: 500,
                    statusText: 'Server Error',
                    headers: {},
                    config,
                    request: {},
                });
            }
            return;
        }

        // Safety net — no handler registered for this URL.
        // Log loudly so missing mocks jump out during development.
        console.warn(`[demo] BLOCKED unmocked request: ${method} ${url}`);
        resolve({
            data: {
                data: null,
                errors: ['This feature is not available in the demo.'],
                meta: { demo: true, blocked: true, method, url },
            },
            status: 200,
            statusText: 'OK',
            headers: {},
            config,
            request: {},
        });
    });
};

/**
 * Install the demo adapter on the global axios instance.
 *
 * Call this once at startup, before any axios calls fire. Idempotent —
 * a second call is a no-op.
 */
let installed = false;
export function installDemoInterceptor() {
    if (installed) return;
    if (!window.axios) {
        console.error('[demo] window.axios not found — bootstrap order is wrong');
        return;
    }
    window.axios.defaults.adapter = demoAdapter;
    installed = true;
    // eslint-disable-next-line no-console
    console.info(
        '[demo] Demo mode active — all backend calls will be intercepted.\n' +
        '[demo] Unmocked requests will return a "not available" response.\n' +
        '[demo] Watch this console for [demo] BLOCKED warnings while exploring.'
    );
}

/**
 * Test-only: clear the registry. Useful if hot-reloading handler modules
 * during development.
 */
export function _resetForTests() {
    handlers.length = 0;
    installed = false;
}
